import {
  ChangeDetectionStrategy,
  Component,
  signal,
  viewChild,
} from '@angular/core';
import type { MenuItem } from '@openng/optimus-ui/api';
import { Menu, MenuModule } from '@openng/optimus-ui/menu';

export interface ActionMenuItem {
  id: string;
  label: string;
  icon?: string;
  materialIcon?: string;
  disabled?: boolean;
  visible?: boolean;
  execute: () => void;
}

@Component({
  selector: 'core-action-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MenuModule],
  template: `
    <p-menu
      #menu
      [popup]="true"
      [model]="menuItems()"
      appendTo="body"
      [style]="{ 'min-width': '0' }"
    >
      <ng-template #item let-item>
        <a
          class="p-menu-item-link"
          tabindex="-1"
          [attr.data-automationid]="item.automationId"
        >
          @if (item.data?.materialIcon) {
            <span
              class="p-menu-item-icon material-icons"
              style="font-size: 1.2rem"
              aria-hidden="true"
              >{{ item.data.materialIcon }}</span
            >
          } @else if (item.icon) {
            <span
              class="p-menu-item-icon"
              [class]="item.icon"
              aria-hidden="true"
            ></span>
          }
          <span class="p-menu-item-label">{{ item.label }}</span>
        </a>
      </ng-template>
    </p-menu>
  `,
})
export class ActionMenuComponent {
  private readonly menu = viewChild.required<Menu>('menu');
  private readonly anchor = signal<HTMLElement | null>(null);
  protected readonly menuItems = signal<MenuItem[]>([]);

  public open(event: MouseEvent, items: ActionMenuItem[]): void {
    const anchor =
      (event.target as HTMLElement).closest('button') ??
      (event.currentTarget as HTMLElement);
    const menuWasOpen = this.menu().visible;
    const sameAnchor = this.anchor() === anchor;
    const anchorEvent = { currentTarget: anchor } as unknown as MouseEvent;

    this.anchor.set(anchor);
    this.menuItems.set(
      items
        .filter(item => item.visible !== false)
        .map(item => ({
          id: item.id,
          label: item.label,
          icon: item.icon,
          data: { materialIcon: item.materialIcon },
          disabled: item.disabled,
          automationId: `action-menu-${item.id}`,
          command: item.execute,
        })),
    );

    if (menuWasOpen && !sameAnchor) {
      this.menu().hide();
      setTimeout(() => this.menu().show(anchorEvent), 0);
      return;
    }

    this.menu().toggle(anchorEvent);
  }
}
